import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-31 — Per-row `{completed}/{total}` collab metric badge for the
 * Brand Mission list.
 *
 * Renders `—/—` when `total === 0` (NOT `0/0`) per PRD Open-Q4 — the
 * dash communicates "no signal yet" rather than "this mission failed."
 * Aria-label spells out the meaning for screen readers.
 */
interface MissionListCollabCountBadgeProps {
  collabCount: { completed: number; total: number }
  className?: string
}

export function MissionListCollabCountBadge({
  collabCount,
  className,
}: MissionListCollabCountBadgeProps) {
  const { t } = useTranslation()
  const total = collabCount?.total ?? 0
  const completed = collabCount?.completed ?? 0

  if (total <= 0) {
    return (
      <Badge
        variant="outline"
        className={cn("font-mono tabular-nums", className)}
        aria-label={t("missions.index.row.collab_count_empty")}
        data-testid="mission-list-collab-count"
      >
        —/—
      </Badge>
    )
  }

  return (
    <Badge
      variant="outline"
      className={cn("font-mono tabular-nums", className)}
      aria-label={t("missions.index.row.collab_count", { completed, total })}
      data-testid="mission-list-collab-count"
    >
      {completed}/{total}
    </Badge>
  )
}
